import { SDK_ERRORS } from '@riz/contracts/sdk';
import { toRizError } from '../core/errors.ts';
import type { SdkClient } from '../core/client.ts';

export interface CatalogueItem {
  sku: string;
  title: string;
  description?: string;
  price: string;
  currency: string;
}

export interface Entitlement {
  id: string;
  sku: string;
  grantedAt: string;
  consumed: boolean;
}

export type PurchaseStatus = 'completed' | 'cancelled' | 'failed';

export interface PurchaseResult {
  status: PurchaseStatus;
  sku: string;
  entitlementId: string | null;
  error?: string;
}

export interface PurchasesModule {
  catalogue(): Promise<CatalogueItem[]>;
  buy(options: { sku: string }): Promise<PurchaseResult>;
  entitlements(): Promise<Entitlement[]>;
  consume(options: { entitlementId: string }): Promise<boolean>;
}

/**
 * Riz holds the catalogue and the entitlement ledger; a payment provider holds
 * the money movement. A purchase is only real once Riz has been notified by the
 * provider, so a game must grant items from `entitlements()` rather than from
 * the return value of `buy()` — the two differ exactly when a payment succeeds
 * and the page closes before the client hears about it.
 */
export function createPurchasesModule(client: SdkClient): PurchasesModule {
  return {
    async catalogue() {
      const capability = client.requireCapability<CatalogueItem[]>('purchases.buy', 'Purchases');
      if (capability) return capability;
      const result = await client.call('command', 'purchases/catalogue', {});
      return Array.isArray(result.items) ? (result.items as CatalogueItem[]) : [];
    },

    async buy({ sku }) {
      const capability = client.requireCapability<PurchaseResult>('purchases.buy', 'Purchases');
      if (capability) return capability;

      if (client.currentPlayer?.isGuest) {
        return {
          status: 'failed',
          sku,
          entitlementId: null,
          error: SDK_ERRORS.notAuthenticated,
        };
      }

      void client.track('purchase_started', { sku }).catch(() => {});
      try {
        const result = await client.call('command', 'purchases/buy', { sku });
        const status = (result.status as PurchaseStatus) ?? 'failed';
        void client.track(
          status === 'completed' ? 'purchase_completed' : status === 'cancelled' ? 'purchase_cancelled' : 'purchase_failed',
          { sku },
        ).catch(() => {});
        return {
          status,
          sku,
          entitlementId: typeof result.entitlementId === 'string' ? result.entitlementId : null,
        };
      } catch (raw) {
        const error = toRizError(raw);
        const cancelled = error.code === SDK_ERRORS.purchaseCancelled;
        void client.track(cancelled ? 'purchase_cancelled' : 'purchase_failed', { sku }).catch(() => {});
        return { status: cancelled ? 'cancelled' : 'failed', sku, entitlementId: null, error: error.code };
      }
    },

    async entitlements() {
      const capability = client.requireCapability<Entitlement[]>('purchases.buy', 'Purchases');
      if (capability) return capability;
      const result = await client.call('command', 'purchases/entitlements', {});
      return Array.isArray(result.entitlements) ? (result.entitlements as Entitlement[]) : [];
    },

    async consume({ entitlementId }) {
      const capability = client.requireCapability<boolean>('purchases.buy', 'Purchases');
      if (capability) return capability;
      const result = await client.call('command', 'purchases/consume', { entitlementId });
      const consumed = result.consumed === true;
      if (consumed) void client.track('entitlement_consumed', { entitlementId }).catch(() => {});
      return consumed;
    },
  };
}
